import { getLangFallbackUrl } from '~/lang/utils/langfetch'
import { getTransformObject } from '~/common/photos/transforms'
import { ReviewFilters, ReviewsInput } from './types'

export class ReviewPhotosFetch {
  async readAll(
    langCode: string,
    { campsiteSlug, filters, order, page, limit }: ReviewsInput,
  ): Promise<ReviewsResponse> {
    const response = await $fetch<AjaxResponse>(
      this.getUrl(langCode, { filters, order, limit, campsiteSlug, page }),
    )
    return {
      ...response,
      page: Number(response.page),
      reviews: this.mapResponseReviews(response.reviews),
    }
  }

  private mapResponseReviews(reviews: AjaxResponseReview[]) {
    return reviews.map((r) => ({
      id: r.id,
      photos: r.photos.map((photo) => ({
        caption: photo.caption,
        url: getTransformObject<PhotoResultUrl>(
          'review',
          photo.url.masterImage,
        ),
        titleAttribution: photo.title_attribution || '',
      })),
      title: r.title,
    }))
  }

  private getUrl(
    langCode: string,
    {
      filters,
      order,
      limit,
      campsiteSlug,
      page,
      reviewId,
    }: {
      filters?: ReviewFilters
      order?: string
      limit?: number
      campsiteSlug: string
      page?: number
      reviewId?: string
    },
  ) {
    const params = new URLSearchParams()
    if (order) {
      params.append('o', order)
    }
    if (limit) {
      params.append('limit', limit.toString())
    }
    if (filters?.rating) {
      params.append('overall', filters.rating)
    }
    if (filters?.groupType) {
      params.append('group_type', filters.groupType)
    }
    if (filters?.unitType) {
      params.append('unit_type', filters.unitType)
    }
    if (filters?.language) {
      params.append('language', filters.language)
    }
    if (reviewId) {
      params.append('review', reviewId)
    }
    if (page && page > 1) {
      params.append('page', page.toString())
    }
    const url = `/_/reviews/campsite/${campsiteSlug}/review-photos/?${params.toString()}`
    return getLangFallbackUrl(langCode, url)
  }
}

interface AjaxResponse {
  count: number
  limit: number
  num_pages: number
  page: string
  reviews: AjaxResponseReview[]
}

interface AjaxResponseReview {
  id: number
  title: string
  photos: ResponsePhotoResult[]
}

interface ResponsePhotoResult {
  caption: string
  url: {
    masterImage: string
  }
  title_attribution?: string
}

interface ReviewsResponse {
  count: number
  limit: number
  num_pages: number
  page: number
  reviews: Review[]
}

interface Review {
  id: number
  photos: PhotoResult[]
  title: string
}

interface PhotoResult {
  caption: string
  url: PhotoResultUrl
  titleAttribution: string
}

interface PhotoResultUrl {
  detailLarge: string
  detailMedium: string
  detailSmall: string
  gallerySmall: string
  galleryMedium: string
  galleryLarge: string
}
